import type {
  Feature,
  GeoJsonProperties,
  Polygon,
  Position,
} from 'geojson'

export type GoogleTerraDrawMode =
  | 'static'
  | 'select'
  | 'polygon'
  | 'circle'
  | 'freehand'

export type GoogleAppDrawMode =
  | 'draw_polygon'
  | 'draw_circle'
  | 'draw_freehand'
  | 'simple_select'

type TerraDrawFeatureProperties = GeoJsonProperties & {
  appDrawMode?: GoogleAppDrawMode | null
  center?: [number, number]
  closingPoint?: boolean
  coordinatePoint?: boolean
  currentlyDrawing?: boolean
  isCircle?: boolean
  midPoint?: boolean
  mode?: string
  radiusInKm?: number
  radiusKilometers?: number
  selectionPoint?: boolean
  snappingPoint?: boolean
}

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function createUuidV4Fallback() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16)
    const value = char === 'x' ? random : (random & 0x3) | 0x8

    return value.toString(16)
  })
}

function createTerraDrawFeatureId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return createUuidV4Fallback()
}

function getTerraDrawFeatureId(feature: Feature) {
  if (typeof feature.id === 'string' && UUID_V4_PATTERN.test(feature.id)) {
    return feature.id
  }

  return createTerraDrawFeatureId()
}

function isPolygonFeature(
  feature: Feature | null | undefined
): feature is Feature<Polygon, GeoJsonProperties> {
  return feature?.geometry?.type === 'Polygon'
}

function isPointOnSegment(
  point: Position,
  start: Position,
  end: Position,
  epsilon = 1e-9
) {
  const [px, py] = point
  const [x1, y1] = start
  const [x2, y2] = end
  const cross = (px - x1) * (y2 - y1) - (py - y1) * (x2 - x1)

  if (Math.abs(cross) > epsilon) return false

  const dot = (px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)
  if (dot < 0) return false

  const lengthSquared = (x2 - x1) ** 2 + (y2 - y1) ** 2
  return dot <= lengthSquared
}

function isPointInRing(point: Position, ring: Position[]) {
  let inside = false

  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index]
    const next = ring[(index + 1) % ring.length]

    if (!current || !next) continue
    if (isPointOnSegment(point, current, next)) return true

    const [xi, yi] = current
    const [xj, yj] = next
    const intersects =
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi

    if (intersects) inside = !inside
  }

  return inside
}

function toFeatureProperties(
  properties: GeoJsonProperties
): TerraDrawFeatureProperties {
  return { ...(properties ?? {}) }
}

function stripCircleProperties(
  properties: TerraDrawFeatureProperties
): TerraDrawFeatureProperties {
  const { center, isCircle, radiusInKm, radiusKilometers, ...rest } =
    properties

  return rest
}

function getFeatureTerraMode(
  feature: Feature<Polygon, GeoJsonProperties>
): Extract<GoogleTerraDrawMode, 'polygon' | 'circle' | 'freehand'> {
  const properties = toFeatureProperties(feature.properties)

  if (
    properties.appDrawMode === 'draw_circle' ||
    properties.isCircle ||
    properties.mode === 'circle'
  ) {
    return 'circle'
  }

  if (
    properties.appDrawMode === 'draw_freehand' ||
    properties.mode === 'freehand'
  ) {
    return 'freehand'
  }

  return 'polygon'
}

export function toTerraDrawMode(drawMode: string | null): GoogleTerraDrawMode {
  if (drawMode === 'draw_polygon') return 'polygon'
  if (drawMode === 'draw_circle') return 'circle'
  if (drawMode === 'draw_freehand') return 'freehand'
  if (drawMode === 'simple_select') return 'select'

  return 'static'
}

export function toAppDrawMode(
  mode: GoogleTerraDrawMode
): GoogleAppDrawMode | null {
  if (mode === 'polygon') return 'draw_polygon'
  if (mode === 'circle') return 'draw_circle'
  if (mode === 'freehand') return 'draw_freehand'
  if (mode === 'select') return 'simple_select'

  return null
}

export function pickPrimaryFeature(features: Feature[]): Feature | null {
  return (
    features.find((feature) => {
      if (!isPolygonFeature(feature)) return false

      const properties = toFeatureProperties(feature.properties)

      return !(
        properties.closingPoint ||
        properties.coordinatePoint ||
        properties.currentlyDrawing ||
        properties.midPoint ||
        properties.selectionPoint ||
        properties.snappingPoint
      )
    }) ?? null
  )
}

export function toTerraDrawFeature(
  feature: Feature | null
): Feature<Polygon, TerraDrawFeatureProperties> | null {
  if (!isPolygonFeature(feature)) return null

  const mode = getFeatureTerraMode(feature)
  const properties = toFeatureProperties(feature.properties)
  const drawProperties =
    mode === 'circle' ? properties : stripCircleProperties(properties)

  return {
    ...feature,
    id: getTerraDrawFeatureId(feature),
    properties: {
      ...drawProperties,
      appDrawMode: toAppDrawMode(mode),
      ...(mode === 'circle' ? { isCircle: true } : {}),
      mode,
    },
  }
}

function getPolygonCenter(
  feature: Feature<Polygon, GeoJsonProperties>
): [number, number] {
  const ring = feature.geometry.coordinates[0] ?? []

  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  for (const coordinate of ring) {
    const [lng, lat] = coordinate

    if (lng < minLng) minLng = lng
    if (lat < minLat) minLat = lat
    if (lng > maxLng) maxLng = lng
    if (lat > maxLat) maxLat = lat
  }

  return [(minLng + maxLng) / 2, (minLat + maxLat) / 2]
}

export function toStorableDrawFeature(
  feature: Feature | null
): Feature<Polygon, TerraDrawFeatureProperties> | null {
  if (!isPolygonFeature(feature)) return null

  const mode = getFeatureTerraMode(feature)
  const properties = toFeatureProperties(feature.properties)
  const drawProperties =
    mode === 'circle' ? properties : stripCircleProperties(properties)

  return {
    ...feature,
    properties: {
      ...drawProperties,
      appDrawMode: toAppDrawMode(mode),
      ...(mode === 'circle'
        ? {
            center: getPolygonCenter(feature),
            isCircle: true,
            radiusInKm: properties.radiusInKm ?? properties.radiusKilometers,
          }
        : {}),
    },
  }
}

export function polygonContainsLngLat(
  feature: Feature | null,
  lngLat: { lng: number; lat: number }
) {
  if (!isPolygonFeature(feature)) return false

  const point: Position = [lngLat.lng, lngLat.lat]
  const [outerRing, ...holes] = feature.geometry.coordinates

  if (!outerRing || !isPointInRing(point, outerRing)) {
    return false
  }

  return !holes.some((ring) => isPointInRing(point, ring))
}
